import React, { useState, useEffect } from 'react';
import NoData from '../../../components/common/NoData';
import Loader from '../../../components/common/Loader';
import { Icon } from '@iconify/react';
import { getTraineeAnalytics } from '../../../services/traineeService';
import { toast } from 'react-toastify';

function TraineeAnalyticsTicketsTab({ traineeId }) {
  const [loading, setLoading] = useState(false);
  const [ticketsData, setTicketsData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const fetchTicketsData = async () => {
    try {
      setLoading(true);
      const response = await getTraineeAnalytics({ trainee_id: traineeId });
      // console.log('Tickets Data---------------------:', response.data.tickets);

      if (response.success) {
        setTicketsData(response.data.tickets || []);
      } else {
        toast.error(response.message || 'Failed to fetch tickets data');
      }
    } catch (error) {
      console.error('Error fetching tickets data:', error);
      toast.error('Something went wrong while fetching tickets data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (traineeId) {
      fetchTicketsData();
    }
  }, [traineeId]);

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case 'solved':
        return 'bg-green-50 border border-green-500 text-green-700 px-2 py-1 rounded';
      case 'pending':
        return 'bg-amber-50 border border-amber-500 text-amber-700 px-2 py-1 rounded';
      case 'in progress':
        return 'bg-blue-50 border border-blue-500 text-blue-700 px-2 py-1 rounded';
      case 'cancelled':
        return 'bg-red-50 border border-red-500 text-red-700 px-2 py-1 rounded';
      default:
        return 'bg-gray-100 border border-gray-400 text-gray-700 px-2 py-1 rounded';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleShowModal = (ticket) => {
    setSelectedTicket(ticket);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedTicket(null);
    setShowModal(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[220px] rounded-2xl border border-gray-100 bg-gray-50/40">
        <Loader />
      </div>
    );
  }

  if (!ticketsData || ticketsData.length === 0) {
    return <NoData message="No tickets found" />;
  }

  return (
    <>
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[980px] w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr className="text-left text-xs font-semibold text-gray-600">
                <th className="px-4 py-3 w-[90px]">SL No.</th>
                <th className="px-4 py-3 w-[180px]">Ticket ID</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Response</th>
                <th className="px-4 py-3 w-[220px]">Created</th>
                <th className="px-4 py-3 w-[140px]">Status</th>
                <th className="px-4 py-3 w-[120px] text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ticketsData.map((ticket, index) => (
                <tr key={ticket.id} className="hover:bg-gray-50/60 transition-colors">
                  <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-gray-900">{ticket.ticket_id}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-900 truncate max-w-[420px]" title={ticket.subject || ''}>
                      {ticket.subject}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {ticket.response ? (
                      <div className="text-gray-600 truncate max-w-[420px]" title={ticket.response || ''}>
                        {ticket.response}
                      </div>
                    ) : (
                      <span className="text-amber-600 font-medium">Not responded yet</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{formatDate(ticket.createdAt)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(ticket.status)}`}>
                      {ticket.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                        title="View Details"
                        onClick={() => handleShowModal(ticket)}
                      >
                        <Icon icon="fluent:eye-24-regular" width="18" height="18" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ticket Details Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 p-4" onClick={handleCloseModal}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h5 className="font-semibold text-lg flex items-center gap-2">
                Ticket Details
                {selectedTicket && (
                  <span className={getStatusBadgeClass(selectedTicket.status)}>
                    {selectedTicket.status}
                  </span>
                )}
              </h5>
              <button type="button" className="p-1 rounded hover:bg-gray-100" onClick={handleCloseModal} aria-label="Close">
                <Icon icon="mdi:close" width="20" height="20" />
              </button>
            </div>

            <div className="p-0 overflow-y-auto flex-1">
              {selectedTicket && (
                <div className="px-4">
                  <div className="border-b border-gray-200 py-3">
                    <div className="grid grid-cols-12 gap-2 mb-2">
                      <div className="col-span-4">
                        <div className="text-gray-500">Ticket ID</div>
                      </div>
                      <div className="col-span-8">
                        <div className="font-medium">{selectedTicket.ticket_id}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-12 gap-2 mb-2">
                      <div className="col-span-4">
                        <div className="text-gray-500">Created Date</div>
                      </div>
                      <div className="col-span-8">
                        <div>{formatDate(selectedTicket.createdAt)}</div>
                      </div>
                    </div>
                    {selectedTicket.updatedAt !== selectedTicket.createdAt && (
                      <div className="grid grid-cols-12 gap-2">
                        <div className="col-span-4">
                          <div className="text-gray-500">Last Updated</div>
                        </div>
                        <div className="col-span-8">
                          <div>{formatDate(selectedTicket.updatedAt)}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="border-b border-gray-200 py-3">
                    <div className="text-gray-500 mb-2">Subject</div>
                    <div>{selectedTicket.subject}</div>
                  </div>

                  <div className="border-b border-gray-200 py-3">
                    <div className="text-gray-500 mb-2">Description</div>
                    <div className="whitespace-pre-wrap">{selectedTicket.description}</div>
                  </div>

                  <div className="py-3">
                    <div className="text-gray-500 mb-2 truncate">Response</div>
                    {selectedTicket.response ? (
                      <div className="whitespace-pre-wrap">{selectedTicket.response}</div>
                    ) : (
                      <div className="text-amber-600">Not responded yet</div>
                    )}
                  </div>

                  {selectedTicket.file_url && (
                    <div className="border-t border-gray-200 py-3">
                      <div className="text-gray-500 mb-2">Attachment</div>
                      <a
                        href={selectedTicket.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 border border-primary text-primary hover:bg-primary/10 font-medium py-2 px-3 rounded-lg text-sm"
                      >
                        <Icon icon="fluent:document-24-regular" width="16" height="16" />
                        View Attachment
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
              <button type="button" className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg" onClick={handleCloseModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default TraineeAnalyticsTicketsTab; 